import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Image as ImageIcon, X, Search, MessageSquare, Check, CheckCheck, Calendar, ClipboardList, CheckCircle2 } from 'lucide-react';
import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Profile, Message, Conversation } from '@/lib/types';
import { useNotificationStore } from '@/store/notificationStore';

const springGentle = { type: 'spring', stiffness: 300, damping: 35 };

function formatMsgTime(ts: string) {
  const d = new Date(ts);
  if (isToday(d)) return format(d, 'h:mm a');
  if (isYesterday(d)) return 'Yesterday ' + format(d, 'h:mm a');
  return format(d, 'MMM d, h:mm a');
}

function formatConvTime(ts: string) {
  return formatDistanceToNow(new Date(ts), { addSuffix: true });
}

export default function Chat() {
  const { partnerId } = useParams();
  const { user, profile: myProfile } = useAuth();
  const navigate = useNavigate();

  const [partners, setPartners] = useState<Profile[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConv, setActiveConv] = useState<Conversation | null>(null);
  const [activePartner, setActivePartner] = useState<Profile | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [uploadingImg, setUploadingImg] = useState(false);
  // Per-partner unread message counts (keyed by partner ID)
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const activeConvIdRef = useRef<string | null>(null);

  // ── Fetch partners, conversations & unread counts ──────────────────────────
  const fetchPartnersAndConvs = useCallback(async () => {
    if (!user) return;
    setLoadingPartners(true);

    const { data: friendships } = await supabase
      .from('friendships')
      .select('requester_id, addressee_id')
      .eq('status', 'accepted')
      .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`);

    if (!friendships?.length) { setLoadingPartners(false); return; }
    const ids = friendships.map(f => f.requester_id === user.id ? f.addressee_id : f.requester_id);

    const { data: profilesData } = await supabase.from('profiles').select('*').in('id', ids);
    setPartners((profilesData as Profile[]) || []);

    const { data: convsData } = await supabase
      .from('conversations')
      .select('*')
      .or(`participant1_id.eq.${user.id},participant2_id.eq.${user.id}`)
      .order('last_message_at', { ascending: false });
    const convs = (convsData as Conversation[]) || [];
    setConversations(convs);

    // Fetch all unread messages across conversations and group by partner
    if (convs.length > 0) {
      const convIds = convs.map(c => c.id);
      const { data: unreadMsgs } = await supabase
        .from('messages')
        .select('conversation_id, sender_id')
        .in('conversation_id', convIds)
        .neq('sender_id', user.id)
        .eq('is_read', false);

      const counts: Record<string, number> = {};
      (unreadMsgs || []).forEach((msg: { conversation_id: string; sender_id: string }) => {
        const conv = convs.find(c => c.id === msg.conversation_id);
        if (!conv) return;
        const pid = conv.participant1_id === user.id ? conv.participant2_id : conv.participant1_id;
        counts[pid] = (counts[pid] || 0) + 1;
      });
      setUnreadCounts(counts);
    }

    setLoadingPartners(false);
  }, [user]);

  useEffect(() => { fetchPartnersAndConvs(); }, [fetchPartnersAndConvs]);

  // Auto-open conversation if partnerId in URL
  useEffect(() => {
    if (partnerId && partners.length > 0) {
      const p = partners.find(pt => pt.id === partnerId);
      if (p) openConversation(p);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId, partners]);

  // ── Open / create a conversation ──────────────────────────────────────────
  async function openConversation(partner: Profile) {
    if (!user) return;
    setActivePartner(partner);
    navigate(`/chat/${partner.id}`, { replace: true });

    let conv = conversations.find(c =>
      (c.participant1_id === user.id && c.participant2_id === partner.id) ||
      (c.participant1_id === partner.id && c.participant2_id === user.id)
    );

    if (!conv) {
      const p1 = user.id < partner.id ? user.id : partner.id;
      const p2 = user.id < partner.id ? partner.id : user.id;
      const { data } = await supabase.from('conversations').upsert(
        { participant1_id: p1, participant2_id: p2 },
        { onConflict: 'participant1_id,participant2_id' }
      ).select().single();
      conv = data as Conversation;
      if (conv) setConversations(prev => [conv!, ...prev]);
    }

    if (!conv) return;
    setActiveConv(conv);
    activeConvIdRef.current = conv.id;

    // Mark all unread messages in this conversation as read
    await markConversationAsRead(conv.id, partner.id);

    loadMessages(conv.id);
    subscribeToMessages(conv.id, partner.id);
  }

  // ── Mark all messages in a conversation as read ───────────────────────────
  async function markConversationAsRead(convId: string, partnerId: string) {
    if (!user) return;
    // Capture BEFORE the await — reading React state after an await risks a stale closure
    const partnerUnread = unreadCounts[partnerId] ?? 0;
    await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('conversation_id', convId)
      .neq('sender_id', user.id)
      .eq('is_read', false);
    // Clear this partner's local sidebar badge
    setUnreadCounts(prev => ({ ...prev, [partnerId]: 0 }));
    // Step 1: instant synchronous decrement → nav badge drops immediately
    useNotificationStore.getState().decrementUnread(partnerUnread);
    // Step 2: async DB refetch → confirms exact count (handles multi-partner totals)
    useNotificationStore.getState().fetchUnreadMessages(user.id);
  }

  // ── Load message history ───────────────────────────────────────────────────
  async function loadMessages(convId: string) {
    setLoadingMsgs(true);
    const { data } = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', convId)
      .order('created_at', { ascending: true });
    setMessages((data as Message[]) || []);
    setLoadingMsgs(false);
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }

  // ── Realtime subscription ─────────────────────────────────────────────────
  function subscribeToMessages(convId: string, partnerId: string) {
    if (channelRef.current) supabase.removeChannel(channelRef.current);
    const channel = supabase
      .channel(`messages:${convId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'messages',
        filter: `conversation_id=eq.${convId}`,
      }, async (payload) => {
        const msg = payload.new as Message;
        setMessages(prev => [...prev, msg]);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        // If the active conversation is open and message is from partner, mark as read instantly
        if (msg.sender_id !== user?.id && activeConvIdRef.current === convId) {
          await supabase.from('messages').update({ is_read: true }).eq('id', msg.id);
          // No badge increment since conversation is open
        } else if (msg.sender_id !== user?.id) {
          // Conversation not active — bump the badge
          setUnreadCounts(prev => ({ ...prev, [partnerId]: (prev[partnerId] || 0) + 1 }));
        }
      })
      .subscribe();
    channelRef.current = channel;
  }

  useEffect(() => {
    return () => { if (channelRef.current) supabase.removeChannel(channelRef.current); };
  }, []);

  // ── Send message ──────────────────────────────────────────────────────────
  async function sendMessage(text?: string, imageUrl?: string) {
    if (!user || !activeConv) return;
    if (!text?.trim() && !imageUrl) return;
    setSending(true);
    const type = text && imageUrl ? 'mixed' : imageUrl ? 'image' : 'text';
    await supabase.from('messages').insert({
      conversation_id: activeConv.id,
      sender_id: user.id,
      content: text?.trim() || null,
      image_url: imageUrl || null,
      message_type: type,
    });
    await supabase.from('conversations').update({ last_message_at: new Date().toISOString() }).eq('id', activeConv.id);
    setMsgText('');
    setSending(false);
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    await sendMessage(msgText);
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploadingImg(true);
    const ext = file.name.split('.').pop();
    const path = `${user.id}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('chat-images').upload(path, file);
    if (error) { toast.error('Image upload failed'); setUploadingImg(false); return; }
    const { data } = supabase.storage.from('chat-images').getPublicUrl(path);
    await sendMessage(msgText || undefined, data.publicUrl);
    setMsgText('');
    setUploadingImg(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const filteredPartners = partners.filter(p =>
    !searchQ || (p.full_name || p.username).toLowerCase().includes(searchQ.toLowerCase())
  );

  const getConv = (pid: string): Conversation | undefined =>
    conversations.find(c =>
      (c.participant1_id === user?.id && c.participant2_id === pid) ||
      (c.participant1_id === pid && c.participant2_id === user?.id)
    );

  return (
    <Layout>
      <div className="h-[calc(100vh-65px)] flex overflow-hidden">
        {/* @section: chat-sidebar */}
        <div className={`w-full md:w-80 flex-shrink-0 border-r border-border flex flex-col ${activePartner ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b border-border">
            <h2 className="font-bold text-foreground mb-3">Messages</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search partners…"
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                className="pl-9 h-9 rounded-xl text-sm"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loadingPartners ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="w-10 h-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-3.5 w-24" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredPartners.length === 0 ? (
              <div className="p-6 text-center">
                <MessageSquare className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm font-medium">No partners yet</p>
                <p className="text-muted-foreground text-xs mt-1">Add partners on the Search page to start chatting</p>
                <Button size="sm" variant="outline" className="mt-3 rounded-xl text-xs" onClick={() => navigate('/search')}>
                  Find Partners
                </Button>
              </div>
            ) : (
              filteredPartners.map(p => {
                const conv = getConv(p.id);
                const isActive = activePartner?.id === p.id;
                const unread = unreadCounts[p.id] || 0;
                return (
                  <button
                    key={p.id}
                    onClick={() => openConversation(p)}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left border-b border-border/50 ${isActive ? 'bg-primary/5 border-l-2 border-l-primary' : ''}`}
                  >
                    <div className="relative flex-shrink-0">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={p.avatar_url ?? undefined} />
                        <AvatarFallback className="bg-primary/15 text-primary font-bold text-sm">
                          {(p.full_name || p.username)?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`text-sm truncate ${unread > 0 ? 'font-bold text-foreground' : 'font-semibold text-foreground'}`}>
                          {p.full_name || p.username}
                        </p>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {conv?.last_message_at && (
                            <span className="text-xs text-muted-foreground">{formatConvTime(conv.last_message_at)}</span>
                          )}
                          {/* Per-partner unread badge */}
                          <AnimatePresence>
                            {unread > 0 && (
                              <motion.span
                                key="unread-badge"
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                                transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                                className="min-w-[18px] h-[18px] rounded-full bg-destructive text-[10px] font-bold text-white flex items-center justify-center px-1 leading-none"
                              >
                                {unread > 9 ? '9+' : unread}
                              </motion.span>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                      <p className={`text-xs truncate mt-0.5 ${unread > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                        @{p.username}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* @section: chat-window */}
        <div className={`flex-1 flex flex-col ${!activePartner ? 'hidden md:flex' : 'flex'}`}>
          {!activePartner ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 text-muted-foreground/20 mx-auto mb-4" />
                <h3 className="font-semibold text-foreground mb-2">Select a conversation</h3>
                <p className="text-muted-foreground text-sm max-w-xs">
                  Choose a partner from the list to start messaging
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div className="px-5 py-3.5 border-b border-border flex items-center gap-3 bg-background">
                <button className="md:hidden p-1.5 rounded-lg hover:bg-muted" onClick={() => { setActivePartner(null); activeConvIdRef.current = null; navigate('/chat'); }}>
                  <X className="w-4 h-4" />
                </button>
                <Avatar className="w-9 h-9">
                  <AvatarImage src={activePartner.avatar_url ?? undefined} />
                  <AvatarFallback className="bg-primary/15 text-primary font-bold text-sm">
                    {(activePartner.full_name || activePartner.username)?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground text-sm">{activePartner.full_name || activePartner.username}</p>
                  <p className="text-xs text-muted-foreground">@{activePartner.username}</p>
                </div>
                <Badge variant="secondary" className="text-xs rounded-lg capitalize">
                  {activePartner.preferred_frequency || 'Partner'}
                </Badge>
              </div>

              {/* Messages area */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" style={{ background: 'var(--background)' }}>
                {loadingMsgs ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                        <Skeleton className={`h-10 rounded-2xl ${i % 2 === 0 ? 'w-48' : 'w-56'}`} />
                      </div>
                    ))}
                  </div>
                ) : messages.length === 0 ? (
                  <AnimatePresence>
                    <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center h-full py-12 text-center">
                      <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                        <MessageSquare className="w-8 h-8 text-primary" />
                      </div>
                      <p className="font-semibold text-foreground mb-1">Start the conversation!</p>
                      <p className="text-muted-foreground text-sm max-w-xs">
                        Send a message to {activePartner.full_name || activePartner.username} and begin your accountability journey together.
                      </p>
                    </motion.div>
                  </AnimatePresence>
                ) : (
                  <>
                    {messages.map((msg, idx) => {
                      const isMe = msg.sender_id === user?.id;
                      const showTime = idx === 0 || (new Date(msg.created_at).getTime() - new Date(messages[idx - 1].created_at).getTime() > 5 * 60 * 1000);
                      return (
                        <div key={msg.id}>
                          {showTime && (
                            <div className="text-center my-3">
                              <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                                {formatMsgTime(msg.created_at)}
                              </span>
                            </div>
                          )}
                          <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={springGentle}
                            className={`flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}
                          >
                            {!isMe && (
                              <Avatar className="w-7 h-7 flex-shrink-0 mb-0.5">
                                <AvatarImage src={activePartner.avatar_url ?? undefined} />
                                <AvatarFallback className="text-xs bg-primary/15 text-primary font-bold">
                                  {(activePartner.full_name || activePartner.username)?.[0]?.toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                            )}
                          <div className={`max-w-[70%] flex flex-col gap-1 ${isMe ? 'items-end' : 'items-start'}`}>
                              {/* ── Task-tag notification card ── */}
                              {msg.message_type === 'task_tag' && msg.content ? (() => {
                                let task: { task_title?: string; task_description?: string; due_date?: string; creator_name?: string; partner_name?: string; message_subtype?: string } = {};
                                try { task = JSON.parse(msg.content); } catch { task = {}; }

                                if (task.message_subtype === 'completion') {
                                  return (
                                    <div className="w-72 rounded-2xl overflow-hidden border-2 border-green-500/30 bg-green-500/5"
                                      style={{ boxShadow: '0 2px 12px color-mix(in srgb, #22c55e 10%, transparent)' }}>
                                      <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border-b border-green-500/20">
                                        <CheckCircle2 className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                                        <span className="text-xs font-bold text-green-700">Task Completion</span>
                                      </div>
                                      <div className="px-3 py-2.5 space-y-1.5">
                                        <p className="text-sm font-semibold text-foreground leading-snug">
                                          {task.task_title || 'Untitled task'}
                                        </p>
                                        <p className="text-xs text-muted-foreground leading-relaxed">
                                          {task.partner_name || 'Your partner'} has marked this task as done! 🎉
                                        </p>
                                      </div>
                                    </div>
                                  );
                                }

                                return (
                                  <div className="w-72 rounded-2xl overflow-hidden border-2 border-primary/30 bg-primary/5"
                                    style={{ boxShadow: '0 2px 12px color-mix(in srgb, var(--primary) 12%, transparent)' }}>
                                    {/* Header strip */}
                                    <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 border-b border-primary/20">
                                      <ClipboardList className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                                      <span className="text-xs font-bold text-primary">You Have Been Added on a Task:</span>
                                    </div>
                                    {/* Body */}
                                    <div className="px-3 py-2.5 space-y-2">
                                      <p className="text-sm font-semibold text-foreground leading-snug">
                                        {task.task_title || 'Untitled task'}
                                      </p>
                                      {task.task_description && (
                                        <p className="text-xs text-muted-foreground leading-relaxed">{task.task_description}</p>
                                      )}
                                      {task.due_date && (
                                        <div className="flex items-center gap-1.5 text-xs text-primary font-medium">
                                          <Calendar className="w-3 h-3" />
                                          Check back in on {format(new Date(task.due_date + 'T12:00:00'), 'MMMM d, yyyy')}
                                        </div>
                                      )}
                                      <p className="text-xs text-muted-foreground italic border-t border-primary/15 pt-2">
                                        Don't forget to mark it as complete if it's done! ✅
                                      </p>
                                    </div>
                                  </div>
                                );
                              })() : (
                                <>
                                  {msg.image_url && (
                                    <img src={msg.image_url} alt="shared"
                                      className="max-w-[240px] rounded-2xl object-cover border border-border"
                                      style={{ maxHeight: 200 }} />
                                  )}
                                  {msg.content && (
                                    <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                                      isMe
                                        ? 'bg-primary text-primary-foreground rounded-br-sm'
                                        : 'bg-muted text-foreground rounded-bl-sm'
                                    }`}
                                      style={isMe ? { boxShadow: '0 2px 8px color-mix(in srgb, var(--primary) 25%, transparent)' } : {}}>
                                      {msg.content}
                                    </div>
                                  )}
                                </>
                              )}
                              {isMe && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  {msg.is_read ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                                </span>
                              )}
                            </div>
                            {isMe && (
                              <Avatar className="w-7 h-7 flex-shrink-0 mb-0.5">
                                <AvatarImage src={myProfile?.avatar_url ?? undefined} />
                                <AvatarFallback className="text-xs bg-primary/15 text-primary font-bold">
                                  {(myProfile?.full_name || myProfile?.username)?.[0]?.toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                            )}
                          </motion.div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* @section: message-input */}
              <div className="px-4 py-3 border-t border-border bg-background">
                <form onSubmit={handleSend} className="flex items-center gap-2">
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImg}
                    className="p-2.5 rounded-xl hover:bg-muted transition-colors text-muted-foreground hover:text-foreground flex-shrink-0"
                  >
                    {uploadingImg
                      ? <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                      : <ImageIcon className="w-5 h-5" />}
                  </button>
                  <Input
                    placeholder={`Message ${activePartner.full_name || activePartner.username}…`}
                    value={msgText}
                    onChange={e => setMsgText(e.target.value)}
                    className="flex-1 h-11 rounded-xl border-border focus-visible:ring-primary/30"
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e as unknown as React.FormEvent); }
                    }}
                  />
                  <Button
                    type="submit"
                    size="icon"
                    className="w-11 h-11 rounded-xl flex-shrink-0"
                    disabled={sending || (!msgText.trim())}
                    style={{ boxShadow: '0 2px 8px color-mix(in srgb, var(--primary) 30%, transparent)' }}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
